import { ReactNode, useState, useEffect, useRef } from 'react'
import Navbar from './Navbar'
import { motion, useReducedMotion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { ParticleBackground } from '@/components/ui/particle-background'
import { NoiseTexture, Vignette, Aurora } from '@/components/ui/animated-background'
import { soundFx } from '@/lib/soundFx'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const prefersReducedMotion = useReducedMotion()
  const location = useLocation()
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const hasPlayedRouteSound = useRef(false)
  const lastHoveredInteractive = useRef<Element | null>(null)

  useEffect(() => {
    if (prefersReducedMotion || window.matchMedia('(pointer: coarse)').matches) {
      return
    }

    let rafId = 0
    const handleMouseMove = (e: MouseEvent) => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY })
      })
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [prefersReducedMotion])

  useEffect(() => {
    const interactiveSelector = [
      'button',
      'a',
      'summary',
      'label',
      '[role="button"]',
      '[role="switch"]',
      '[role="tab"]',
      '[data-sfx-click]',
      'input[type="checkbox"]',
      'input[type="radio"]',
      'input[type="submit"]',
      'input[type="button"]',
    ].join(',')

    const resolveInteractive = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null
      return target.closest(interactiveSelector)
    }

    const ensureAudioReady = () => {
      void soundFx.resume()
    }

    const handlePointerDown = (event: PointerEvent) => {
      ensureAudioReady()
      const interactive = resolveInteractive(event.target)
      if (!interactive) return

      if (
        interactive instanceof HTMLInputElement &&
        (interactive.type === 'checkbox' || interactive.type === 'radio')
      ) {
        soundFx.toggle(!interactive.checked)
        return
      }

      if (interactive.getAttribute('role') === 'switch') {
        const checked = interactive.getAttribute('aria-checked') === 'true'
        soundFx.toggle(!checked)
        return
      }

      soundFx.click()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      ensureAudioReady()
      const interactive = resolveInteractive(event.target)
      if (!interactive) return
      soundFx.click()
    }

    const handlePointerOver = (event: PointerEvent) => {
      if (event.pointerType && event.pointerType !== 'mouse') return
      const interactive = resolveInteractive(event.target)
      if (!interactive || lastHoveredInteractive.current === interactive) return
      lastHoveredInteractive.current = interactive
      soundFx.hover()
    }

    const handlePointerOut = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (lastHoveredInteractive.current && target.contains(lastHoveredInteractive.current)) {
        lastHoveredInteractive.current = null
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('pointerover', handlePointerOver, true)
    document.addEventListener('pointerout', handlePointerOut, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('pointerover', handlePointerOver, true)
      document.removeEventListener('pointerout', handlePointerOut, true)
    }
  }, [])

  useEffect(() => {
    if (!hasPlayedRouteSound.current) {
      hasPlayedRouteSound.current = true
      return
    }
    soundFx.navigate()
  }, [location.pathname, location.search, location.hash])

  return (
    <div className="min-h-screen bg-deep-black relative overflow-x-hidden">
      {/* Particle Background */}
      <ParticleBackground
        particleCount={40}
        connectionDistance={120}
        particleColors={["#B8A77D", "#C9B896", "#E8E2D8"]}
      />

      {/* Aurora Background Effect */}
      <Aurora intensity="medium" />

      {/* Noise texture overlay */}
      <NoiseTexture opacity={0.015} />

      {/* Vignette effect */}
      <Vignette intensity="medium" />

      {/* Mouse spotlight effect */}
      {!prefersReducedMotion ? (
        <motion.div
          className="fixed pointer-events-none z-0"
          style={{
            left: mousePosition.x - 200,
            top: mousePosition.y - 200,
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(184, 167, 125, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
          animate={{
            left: mousePosition.x - 200,
            top: mousePosition.y - 200,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 35,
          }}
        />
      ) : null}

      {/* Navigation */}
      <Navbar />

      {/* Main content with lightweight transitions */}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
        className="relative z-10"
      >
        {children}
      </motion.main>

      {/* Ambient glow effects */}
      <div className="fixed bottom-0 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl pointer-events-none animate-float" />
      <div className="fixed top-1/3 right-0 w-64 h-64 bg-gold/3 rounded-full blur-3xl pointer-events-none animate-float" style={{ animationDelay: '2s' }} />
      <div className="fixed top-1/4 left-0 w-48 h-48 bg-gold/4 rounded-full blur-3xl pointer-events-none animate-float" style={{ animationDelay: '4s' }} />
    </div>
  )
}
